import React, { useEffect, useState } from 'react';
import { 
    DocumentCard, 
    DocumentCardDetails, 
    DocumentCardType, 
    IDocumentCardStyles, 
    Text,
    ITextStyles,
    Stack,
    StackItem, 
    Image,
    IImageStyles,
    IStackStyles,
} from '@fluentui/react';
import LoaderComponent from '../../components/loader/loader.component';
import { MapContainer, TileLayer } from 'react-leaflet';
import { LatLng } from 'leaflet';
import LeafletMapMarkerOrderComponent from '../../components/map-marker/leaflet-map-marker-order.component';
import { AzureDataPosition, AzureMap, AzureMapDataSourceProvider, AzureMapsProvider } from 'react-azure-maps';
import { AuthenticationType } from 'azure-maps-control';
import AzureMapMarkerOrderComponent from '../../components/map-marker/azure-map-marker-order.component';
import GoogleMapsMapComponent from '../../components/google-maps/google-maps-map.component';
import GoogleMapMarkerOrderComponent from '../../components/map-marker/google-map-marker-order.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import queryString from "query-string";
import { Buffer } from "buffer";

import ApiService from '../../services/api/api.service';

import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemRegistroTiempo from '../../models/ItemRegistroTiempo';
import ItemTarea from '../../models/ItemTarea';
import ItemUsuario from '../../models/ItemUsuario';

import logo from '../../assets/Icono MyMov blanco.png';

import 'leaflet/dist/leaflet.css';

const getLeafletLatLng = (value: string) : LatLng => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return new LatLng(parseFloat(valueArray[0]), parseFloat(valueArray[1]));
    }
    else {
        const valueArray : string[] = value.split(';');
        return new LatLng(parseFloat(valueArray[0]), parseFloat(valueArray[1]));
    }
};

const getAzureLatLng = (value: string) : AzureDataPosition => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return [parseFloat(valueArray[1]), parseFloat(valueArray[0])];
    }
    else {
        const valueArray : string[] = value.split(';');
        return [parseFloat(valueArray[1]), parseFloat(valueArray[0])];
    }
};

const getGoogleLatLng = (value: string) : google.maps.LatLngLiteral => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return {
            lat: parseFloat(valueArray[0]),
            lng: parseFloat(valueArray[1]),
        };
    }
    else {
        const valueArray : string[] = value.split(';');
        return {
            lat: parseFloat(valueArray[0]),
            lng: parseFloat(valueArray[1]),
        };
    }
};

const cardStyle: IDocumentCardStyles = {
    root: {
        backgroundColor: "#ffffff",
        minWidth: "calc(100% - 20px)",
        padding: 10,
        color: "rgb(50, 49, 48)",
        height: 'auto',
    },
};

const innerCardStyle: IDocumentCardStyles = {
    root: {
        backgroundColor: "#ffffff",
        minWidth: "calc(100% - 22px)",
        padding: 10,
        color: "rgb(50, 49, 48)",
        height: 'auto',
    },
};

const selectedCardStyle: IDocumentCardStyles = {
    root: {
        backgroundColor: "#ed6d63",
        minWidth: "calc(100% - 20px)",
        padding: 10,
        borderColor: "#ed6d63",
        color: "#ffffff",
        height: 'auto',
        selectors: {
            ":hover": {
                borderColor: "#ed6d63",
            },
        },
    },
};

const logoStyles: IImageStyles = {
    root: {
        backgroundColor: 'var(--color-primario)',
        borderRadius: 32,
        padding: 5,
        marginRight: 10,
    },
    image: {},
};

const grayTextStyles: ITextStyles = {
    root: {
        color: "#999999",
    },
};

const stackSpaceBetweenStyles : IStackStyles = {
    root: {
        justifyContent: 'space-between',
    },
};

interface IChronologyOrdersReportPageProps {};

interface IChronologyOrdersReportPageParams {
    IdUsuario: string;
    CodigoCliente: string;
    TokenSesion: string;
    IdOrden: number;
};

const ChronologyOrdersReportPage = (props: IChronologyOrdersReportPageProps) => {
    const mapType : string = "google";

    const [params, setParams] = useState<IChronologyOrdersReportPageParams>();
    const [user, setUser] = useState<ItemUsuario>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [workOrder, setWorkOrder] = useState<ItemOrdenDeTrabajo>();
    const [selectedWork, setSelectedWork] = useState<ItemTarea>();
    const [selectedWorkPart, setSelectedWorkPart] = useState<ItemRegistroTiempo>();

    useEffect(() => {
        let queryParams : queryString.ParsedQuery<string> = queryString.parse(window.location.search);
        setParams(JSON.parse(Buffer.from(queryParams.ItemCronologia?.toString() || "", 'base64').toString('ascii')));
    }, []);

    useEffect(() => {
        if (params) {
            let user : ItemUsuario = new ItemUsuario();
            user.IdUsuario = params.IdUsuario;
            user.CodigoCliente = params.CodigoCliente;
            user.Token = params.TokenSesion;
            setUser(user);
        }
    }, [params]);

    useEffect(() => {
        if (user && params) {
            setLoading(true);
            setError('');
            ApiService.Post<ItemOrdenDeTrabajo>('OrdenesTrabajo', 'Get', user, {
                "IdOrden": params.IdOrden,
            }, true).then((response: ItemOrdenDeTrabajo) => {
                setWorkOrder(response);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    }, [user, params]);

    const onRowClick = (item: ItemRegistroTiempo) : void => {
        setSelectedWorkPart(item);
    };

    const onSelectMapMarker = (item: ItemRegistroTiempo) : void => {
        setSelectedWorkPart(item);
    };

    useEffect(() => {
        if (workOrder && selectedWorkPart) {
            setSelectedWork(workOrder.ListaTareas.find((work: ItemTarea) => {
                return (work.Id === selectedWorkPart.IdTarea);
            }));
        }
        else {
            setSelectedWork(undefined);
        }
    }, [workOrder, selectedWorkPart]);

    const getTimeByWork = (work: ItemTarea) : number => {
        if (workOrder) {
            return workOrder.ListaPartesTrabajo.filter((workPart: ItemRegistroTiempo) => {
                return (workPart.IdTarea === work.Id);
            }).map((workPart: ItemRegistroTiempo) => {
                return parseFloat(workPart.TiempoEmpleado.toString());
            }).reduce((previous: number, current: number) => {
                return previous + current;
            }, 0);
        }
        else {
            return 0;
        }
    };

    const getTimeByNumber = (value: number) : string => {
        let minutes : number = value * 60;
        let hours : number = Math.floor((minutes / 60));
        minutes = minutes - (hours * 60);
        return hours.toString() + ":" + parseInt(minutes.toString()).toString();
    } ;

    return (
        <div className='d-flex flex-column h-100 w-100'>
            {
                (!loading && !error) ? (
                    <div className="row m-0 flex-fill overflow-hidden">
                        <div className="col-12 col-md-6 h-50 h-md-100 px-2 overflow-auto">
                            <div className="row m-0">
                                <div className="col-12 py-3">
                                    {
                                        workOrder ? (
                                            <>
                                                <DocumentCard 
                                                    styles={cardStyle}
                                                    type={DocumentCardType.compact}>
                                                    <DocumentCardDetails>
                                                        <Stack horizontal={true}>
                                                            <StackItem>
                                                                <Image 
                                                                    styles={logoStyles}
                                                                    src={logo}
                                                                    width={32} 
                                                                    height={32} 
                                                                    alt="Icon" />
                                                            </StackItem>
                                                            <StackItem>
                                                                <Text>
                                                                    <strong>
                                                                        {workOrder.NumeroDocumento} | {workOrder.Para}
                                                                    </strong>
                                                                </Text>
                                                                <br/>
                                                                <Text styles={grayTextStyles}>
                                                                    {workOrder.Nombre}
                                                                </Text>
                                                                <br/>
                                                                <Text>
                                                                    {workOrder.Descripcion}
                                                                </Text>
                                                            </StackItem>
                                                        </Stack>
                                                    </DocumentCardDetails>
                                                </DocumentCard>
                                                {
                                                    workOrder.ListaTareas.map((work: ItemTarea) => 
                                                        <DocumentCard 
                                                            key={work.Id}
                                                            styles={cardStyle}
                                                            type={DocumentCardType.compact}>
                                                            <DocumentCardDetails>
                                                                <Stack>
                                                                    <StackItem>
                                                                        <Stack 
                                                                            horizontal={true}
                                                                            styles={stackSpaceBetweenStyles}>
                                                                            <StackItem>
                                                                                <Text>
                                                                                    <strong>
                                                                                        {work.Descripcion}
                                                                                    </strong>
                                                                                </Text>
                                                                                <br/>
                                                                                <Text styles={grayTextStyles}>
                                                                                    {work.Nombre}
                                                                                </Text>
                                                                            </StackItem>
                                                                            <StackItem styles={{
                                                                                root: {
                                                                                    minWidth: 200,
                                                                                    textAlign: 'right',
                                                                                },
                                                                            }}>
                                                                                {getTimeByNumber(getTimeByWork(work))} h
                                                                            </StackItem>
                                                                        </Stack>
                                                                    </StackItem>
                                                                    <StackItem>
                                                                        <br/>
                                                                        {
                                                                            workOrder.ListaPartesTrabajo.filter((workPart: ItemRegistroTiempo) => {
                                                                                return (workPart.IdTarea === work.Id);
                                                                            }).map((workPart: ItemRegistroTiempo) =>
                                                                                workPart.PosicionGPSInicio ? (
                                                                                    <DocumentCard 
                                                                                        key={workPart.Id}
                                                                                        styles={(workPart.Id === selectedWorkPart?.Id) ? selectedCardStyle : innerCardStyle}
                                                                                        type={DocumentCardType.compact}
                                                                                        onClick={() => onRowClick(workPart)}>
                                                                                        <DocumentCardDetails>
                                                                                            <Stack>
                                                                                                <StackItem>
                                                                                                    <Stack 
                                                                                                        horizontal={true}
                                                                                                        styles={stackSpaceBetweenStyles}>
                                                                                                        <StackItem>
                                                                                                            {workPart.Descripcion}
                                                                                                        </StackItem>
                                                                                                        <StackItem styles={{
                                                                                                            root: {
                                                                                                                minWidth: 200,
                                                                                                                textAlign: 'right',
                                                                                                            },
                                                                                                        }}>
                                                                                                            {getTimeByNumber(workPart.TiempoEmpleado)} h
                                                                                                        </StackItem>
                                                                                                    </Stack>
                                                                                                </StackItem>
                                                                                            </Stack>
                                                                                        </DocumentCardDetails>
                                                                                    </DocumentCard>
                                                                                ) : (
                                                                                    null
                                                                                )
                                                                            )
                                                                        }
                                                                    </StackItem>
                                                                </Stack>
                                                            </DocumentCardDetails>
                                                        </DocumentCard>
                                                    )
                                                }
                                            </>
                                        ) : (
                                            null
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 h-50 h-md-100 overflow-hidden px-2">
                            {
                                (selectedWork && selectedWorkPart) ? (
                                    (mapType === "leaflet") ? (
                                        <MapContainer 
                                            center={getLeafletLatLng(selectedWorkPart.PosicionGPSInicio)} 
                                            zoom={13}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                minHeight: 400,
                                            }}>
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                            <LeafletMapMarkerOrderComponent
                                                key={selectedWorkPart.Id}
                                                work={selectedWork}
                                                workPart={selectedWorkPart}
                                                selectedWorkPart={selectedWorkPart}
                                                selectedStart={true}
                                                onSelect={onSelectMapMarker} />
                                        </MapContainer>
                                    ) : (mapType === "azure") ? (
                                        <AzureMapsProvider>
                                            <div style={{ 
                                                width: '100%',
                                                height: '100%',
                                                minHeight: 400,
                                            }}>
                                                <AzureMap 
                                                    options={{
                                                        authOptions: {
                                                            authType: AuthenticationType.subscriptionKey,
                                                            subscriptionKey: '3Qqlzj_T3oymm37vAdIMIs2H4jUbPZXxtbkHg03LONw',
                                                        },
                                                        center: getAzureLatLng(selectedWorkPart.PosicionGPSInicio),
                                                        zoom: 13,
                                                    }}>
                                                    <AzureMapDataSourceProvider 
                                                        id={'AzureMapDataSourceProvider'}
                                                        options={{
                                                            lineMetrics: true,
                                                        }}>
                                                        <AzureMapMarkerOrderComponent
                                                            key={selectedWorkPart.Id}
                                                            work={selectedWork}
                                                            workPart={selectedWorkPart}
                                                            selectedWorkPart={selectedWorkPart}
                                                            selectedStart={true}
                                                            onSelect={onSelectMapMarker} />
                                                    </AzureMapDataSourceProvider>
                                                </AzureMap>
                                            </div>
                                        </AzureMapsProvider>
                                    ) : (mapType === "google") ? (
                                        <GoogleMapsMapComponent
                                            zoom={13}
                                            center={getGoogleLatLng(selectedWorkPart.PosicionGPSInicio)}>
                                            <GoogleMapMarkerOrderComponent
                                                key={selectedWorkPart.Id}
                                                work={selectedWork}
                                                workPart={selectedWorkPart}
                                                selectedWorkPart={selectedWorkPart}
                                                selectedStart={true}
                                                onSelect={onSelectMapMarker} />
                                        </GoogleMapsMapComponent>
                                    ) : (
                                        null
                                    )
                                ) : (
                                    null
                                )
                            }
                        </div>
                    </div>
                ) : (
                    null
                )
            }
            {
                !loading ? (
                    <ErrorMessageComponent message={error} />
                ) : (
                    null
                )
            }
            <LoaderComponent loading={loading} />
        </div>
    );
};

export default ChronologyOrdersReportPage;
